<template>
  <div :class="`${classes} ${dropdown ? 'dropdown' : ''} ${hasBtnGroup ? 'btn-group' : ''}`">
    <button
      v-if="variant === 'raised'"
      class="btn"
      :disabled="disabled"
      :type="type || 'button'"
      :class="raisedAndOutlinedClasses"
      :data-toggle="hasJustCaretOnBtn && !openDropdownOnHover ? 'dropdown' : ''"
      @click="buttonAction"
    >
      <tr-svg-icon
        v-if="svgIconName"
        :classes="label ? 'vertical-align-sub margin-right-10' : ''"
        :color="svgIconColor"
        size="small"
        :name="svgIconName"
      />
      <slot name="label" />
      {{ label }}
      <span v-if="hasJustCaretOnBtn && !hideCaret">
        &nbsp;
        <span
          class="caret"
        />
      </span>
    </button>
    <button
      v-if="variant === 'outlined'"
      class="btn"
      :disabled="disabled"
      :type="type || 'button'"
      :class="raisedAndOutlinedClasses"
      :data-toggle="hasJustCaretOnBtn ? 'dropdown' : ''"
      @click="buttonAction"
    >
      <tr-svg-icon
        v-if="svgIconName"
        :color="svgIconColor"
        size="small"
        :name="svgIconName"
      />
      <slot name="label" />
      {{ label }}
      <span v-if="hasJustCaretOnBtn && !openDropdownOnHover && !hideCaret">
        &nbsp;
        <span
          class="caret"
        />
      </span>
    </button>
    <a
      v-else-if="type !== 'file' && (!variant || variant === 'text')"
      class="no-margin cursor-pointer inline-block"
      :disabled="disabled || undefined"
      :class="`${classes || ''} ${linkColor ? '' : 'h3'} ${dropdown ? 'dropdown-toggle' : ''} ${dropdown && !hideCaret ? 'vertical-align-super' : ''}`"
      :data-toggle="dropdown && !openDropdownOnHover ? 'dropdown' : ''"
      role="button"
      @click="buttonAction"
    >
      <div class="flex-row">
        <tr-typography :variant="textVariant || 'h3'">
          <span :class="`${(dropdown && !hideCaret) || normalAlignment ? '' : 'vertical-align-super'} ${svgPosition ? `margin-${svgPosition}-10` : '' }`">
            <slot name="label" />
            {{ label }}
          </span>
          <svg
            v-if="svgHref"
            aria-labelledby="title"
            role="img"
            :class="`ic--${svgColor ? svgColor : 'green'} ic--${svgSize ? svgSize : 'medium'} ${svgPosition ? `pull-${svgPosition}` : ''} vertical-align-sub`"
          >
            <use :xlink:href="'#' + svgHref">
              <title>{{ label }}</title>
            </use>
          </svg>
          <span
            v-if="dropdown && !hideCaret"
            class="caret left-spacing"
          />
        </tr-typography>
      </div>
    </a>
    <button
      v-if="hasBtnGroup && variant !== 'text'"
      class="btn dropdown-toggle btn-group-dropdown"
      :class="`btn-${color || 'default'} btn-lg`"
      :disabled="disabled"
      type="button"
      :data-toggle="'dropdown' || ''"
      aria-haspopup="true"
      aria-expanded="false"
    >
      <span class="caret" />
    </button>
    <ul
      v-if="dropdown && !$slots.dropdownMenu"
      class="dropdown-menu"
    >
      <slot />
    </ul>
    <slot name="dropdownMenu" />
    <label
      v-if="type === 'file'"
      for="importFileInput"
      :class="`btn ${raisedAndOutlinedClasses}`"
    >
      {{ label }}
    </label>
    <input
      v-if="type === 'file'"
      id="importFileInput"
      type="file"
      :accept="accept"
      style="display: none;"
    >
  </div>
</template>

<script>
import TrSvgIcon from './TrSvgIcon.vue';
import TrTypography from './TrTypography.vue';
import jQuery from 'jquery';

const BUTTON_SIZES = {
  xLarge: 'x-lg',
  large: 'lg',
  medium: 'md',
  small: 'sm'
};

const component = {
  name: 'tr-button',
  props: ['label', 'svgHref', 'onButtonClick',
    'variant', 'type', 'color', 'classes',
    'svgIconName', 'href', 'dropdown',
    'svgIconColor', 'size', 'textVariant',
    'blockSize', 'openDropdownOnHover',
    'hideCaret', 'disabled', 'linkColor',
    'svgSize', 'svgColor', 'svgPosition',
    'normalAlignment', 'openInNewTab', 'buttonClasses',
    'onFilesSelect', 'routeName', 'accept'],
  components: {
    TrSvgIcon,
    TrTypography
  },
  mounted() {
    if (this.openDropdownOnHover) {
      const dropdownContainer = this.$el;
      dropdownContainer.addEventListener('mouseenter', this.show);
      dropdownContainer.addEventListener('mouseleave', this.hide);
    }

    if (this.type === 'file') {
      const inputFile = jQuery(this.$el.querySelector('input[type=file]'));
      inputFile.off('change');
      inputFile.on('change', this.onInputFileChange);
    }
  },
  destroy() {
    if (this.openDropdownOnHover) {
      const dropdownContainer = this.$el;
      dropdownContainer.removeEventListener('mouseenter', this.show);
      dropdownContainer.removeEventListener('mouseleave', this.hide);
    }
  },
  computed: {
    hasOwnAction() {
      return Boolean(this.href ||
        this.onButtonClick ||
        this.type === 'submit');
    },
    hasBtnGroup() {
      return Boolean(this.hasOwnAction && this.dropdown && !this.openDropdownOnHover);
    },
    hasJustCaretOnBtn() {
      return Boolean(!this.hasOwnAction && this.dropdown);
    },
    buttonAction() {
      return () => {
        if (this.href) {

          if (this.openInNewTab) {
            window.open(this.href, '_blank');

            if (this.onButtonClick) {
              this.onButtonClick();
            }

            return;
          }

          return window.location.href = this.href;
        }

        if (this.routeName) {
          this.$router.push({ name: this.routeName });
        }

        if (this.type !== 'submit' && this.onButtonClick) {
          return this.onButtonClick();
        }
      }
    },
    raisedAndOutlinedClasses() {
      let classes = '';

      classes += ` btn-${this.color || 'default'} `;
      classes += ` btn-${BUTTON_SIZES[this.size] || 'lg'} `;
      classes += ` ${this.svgIconName ? 'btn--icon' : ''} `;
      classes += ` ${this.blockSize ? 'btn-block' : ''} `;
      classes += ` ${this.buttonClasses} `;

      if (!this.hasOwnAction && this.dropdown) {
        classes += ' dropdown-toggle ';
      }

      return classes;
    }
  },
  methods: {
    onInputFileChange(e) {
      this.onFilesSelect(e.target.files);
    },
    show() {
      const dropdown = this._getDropdown();
      if (dropdown) {
        dropdown.classList.add('open');
      }
    },
    hide() {
      const dropdown = this._getDropdown();
      if (dropdown) {
        dropdown.classList.remove('open');
      }
    },
    _getDropdown() {
      return this.$el && this.$el.closest('.dropdown');
    }
  }
};

export default component;
</script>

<style scoped>
  span .left-spacing {
    margin-left: 10px;
  }
</style>